import { Upload, X } from "lucide-react";
import React from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type TUploadValue =
  | File
  | File[]
  | string
  | string[]
  | (File | string)[]
  | null;

function isNonEmptyString(v: unknown): v is string {
  return typeof v === "string" && v.length > 0;
}

function fileNameFromUrl(url: string): string {
  try {
    const path = url.split("?")[0] ?? url;
    const last = path.split("/").pop();
    return last && last.length > 0 ? decodeURIComponent(last) : url;
  } catch {
    return url;
  }
}

interface FileUploadProps {
  label: string;
  description?: string;
  accept?: string;
  multiple?: boolean;
  value?: TUploadValue;
  onChange?: (file: TUploadValue) => void;
  onBlur?: () => void;
  name?: string;
  error?: boolean;
  className?: string;
}

export const FileUpload = React.forwardRef<HTMLInputElement, FileUploadProps>(
  (
    {
      label,
      description,
      accept = "*/*",
      multiple = false,
      value,
      onChange,
      onBlur,
      name,
      error,
      className,
    },
    ref
  ) => {
    const inputRef = React.useRef<HTMLInputElement | null>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        if (multiple) {
          // Para múltiplos arquivos, adicionar aos arquivos existentes
          const newFiles = Array.from(files);
          const existing = Array.isArray(value) ? value : [];
          const existingFiles = existing.filter(
            (v): v is File => v instanceof File
          );
          const existingUrls = existing.filter(
            (v): v is string => isNonEmptyString(v)
          );
          const combinedFiles = [
            ...existingUrls,
            ...existingFiles,
            ...newFiles,
          ];
          onChange?.(combinedFiles);
        } else {
          // Para arquivo único
          onChange?.(files[0]);
        }
      }
      // Resetar o input para permitir selecionar os mesmos arquivos novamente
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    };

    const handleRemoveFile = (fileToRemove?: File | string) => {
      if (multiple && Array.isArray(value)) {
        if (fileToRemove !== undefined) {
          // Remove arquivo ou URL específica
          const newFiles = value.filter((item) => item !== fileToRemove);
          onChange?.(newFiles.length > 0 ? newFiles : null);
        } else {
          // Remove todos os arquivos
          onChange?.(null);
        }
      } else {
        // Remove arquivo único
        onChange?.(null);
      }

      if (inputRef.current) {
        inputRef.current.value = "";
      }
    };

    const handleClick = () => {
      inputRef.current?.click();
    };

    // Verificar se há arquivos para mostrar
    const hasFiles = Boolean(
      value && (Array.isArray(value) ? value.length > 0 : true)
    );

    return (
      <div className={cn("space-y-2 min-w-0", className)}>
        <label className="text-sm font-medium leading-none text-foreground peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          {label}
        </label>

        <div
          className={cn(
            "border-2 border-dashed rounded-lg p-4 text-center transition-colors min-w-0 overflow-hidden",
            error
              ? "border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950"
              : "border-gray-300 hover:border-gray-400 dark:border-gray-700 dark:hover:border-gray-600"
          )}
        >
          {hasFiles ? (
            <div className="space-y-4">
              {multiple && Array.isArray(value) ? (
                // Renderização para múltiplos arquivos
                <div className="space-y-3">
                  {value.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-muted rounded-lg"
                    >
                      <div className="flex items-center space-x-2 min-w-0">
                        <Upload className="h-4 w-4 shrink-0 text-primary" />
                        {isNonEmptyString(item) ? (
                          <a
                            href={item}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-medium text-primary truncate hover:underline"
                          >
                            {fileNameFromUrl(item)}
                          </a>
                        ) : (
                          <span className="text-sm font-medium text-foreground truncate">
                            {(item as File).name}
                          </span>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveFile(item)}
                        className="h-6 w-6 p-0 shrink-0"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex flex-col gap-2 w-full">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleClick}
                      className="w-full"
                    >
                      Adicionar mais arquivos
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveFile()}
                      className="w-full text-destructive hover:text-destructive"
                    >
                      Remover todos
                    </Button>
                  </div>
                </div>
              ) : (
                // Renderização para arquivo único
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center space-x-2 min-w-0">
                      <Upload className="h-5 w-5 shrink-0 text-primary" />
                      {isNonEmptyString(value) ? (
                        <a
                          href={value}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-medium text-primary truncate hover:underline"
                        >
                          {fileNameFromUrl(value)}
                        </a>
                      ) : (
                        <span className="text-sm font-medium text-foreground truncate">
                          {(value as File).name}
                        </span>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveFile()}
                      className="h-8 w-8 p-0 shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  {value instanceof File && (
                    <p className="text-xs text-muted-foreground">
                      {(value.size / 1024).toFixed(2)} KB
                    </p>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <Upload className="h-10 w-10 text-primary mx-auto" />
              <div>
                <p className="text-sm font-medium text-foreground">{label}</p>
                {description && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {description}
                  </p>
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={handleClick}
                className="mt-2"
              >
                {multiple ? "Selecionar arquivos" : "Selecionar arquivo"}
              </Button>
            </div>
          )}
        </div>

        <input
          ref={(node) => {
            // Handle both refs
            if (typeof ref === "function") {
              ref(node);
            } else if (ref) {
              (ref as React.MutableRefObject<HTMLInputElement | null>).current =
                node;
            }
            (inputRef as React.MutableRefObject<HTMLInputElement | null>).current =
              node;
          }}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleFileChange}
          onBlur={onBlur}
          name={name}
          className="hidden"
        />
      </div>
    );
  }
);

FileUpload.displayName = "FileUpload";
